import { inject, Injectable, OnDestroy } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { NavigationCancel, NavigationEnd, NavigationError, Router } from '@angular/router';
import { DeviceInfoService } from '@core/services/device-info.service';
import { NzDrawerComponent, NzDrawerOptions, NzDrawerService } from 'ng-zorro-antd/drawer';
import { NzModalService, ModalOptions } from 'ng-zorro-antd/modal';
import { Observable, Subscription, take } from 'rxjs';

export const MOBILE_DIALOG_BACK_BUTTON_DISABLED_CLASS = 'mobile-dialog-back-button-disabled';

export interface MobileDialogBackButtonData {
  mobileBackButtonClose?: boolean;
}

export interface MobileOverlayRegistration {
  isOpen: () => boolean;
  // Called on device Back; return false if the overlay refused to close.
  close: () => boolean;
  closed: Observable<unknown>;
  // false = Back never closes it; it only swallows Back while a dismissable overlay is below it.
  dismissable?: boolean;
  // Replaces the library closeOnNavigation, which our own history.back() would trigger.
  navigationClose?: () => void;
}

interface MobileOverlayEntry {
  openedHref: string;
  dismissable: boolean;
  isOpen: () => boolean;
  close: () => boolean;
  navigationClose?: () => void;
}

// Phone only: one same-URL "placeholder" history entry exists while any dismissable overlay is open.
@Injectable({ providedIn: 'root' })
export class MobileDialogBackButtonService implements OnDestroy {
  private readonly deviceInfoService = inject(DeviceInfoService);
  private readonly router = inject(Router);
  private readonly historyStateKey = 'mobileDialogOpen';
  private readonly entries: MobileOverlayEntry[] = [];
  private readonly popStateHandler = (): void => this.handlePopState();

  private initialized = false;
  private idCounter = 0;
  private placeholderId: string | null = null;
  private placeholderHref: string | null = null;
  private removingPlaceholder = false;
  private skipDeadPlaceholder = false;
  private syncTimeoutId: number | null = null;
  private lastPageId: unknown = null;
  private routerSubscription?: Subscription;

  public init(): void {
    if (this.initialized) {
      return;
    }

    this.initialized = true;
    this.lastPageId = this.getPageId();
    this.patchMatDialogOpen();
    this.patchNzModalCreate();
    this.patchNzDrawerCreate();
    window.addEventListener('popstate', this.popStateHandler);

    this.routerSubscription = this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError) {
        this.lastPageId = this.getPageId();

        if (this.skipDeadPlaceholder) {
          this.skipDeadPlaceholder = false;

          if (history.state?.[this.historyStateKey] && !this.hasDismissableEntries()) {
            history.back();
            return;
          }
        }

        if (this.placeholderId) {
          this.scheduleSync();
        }
      }
    });
  }

  public ngOnDestroy(): void {
    window.removeEventListener('popstate', this.popStateHandler);
    this.routerSubscription?.unsubscribe();
  }

  public isPhone(): boolean {
    return this.deviceInfoService.getInfo().deviceTypeDetected === 'PHONE';
  }

  public registerOpenOverlay(opts: MobileOverlayRegistration): void {
    const entry: MobileOverlayEntry = {
      openedHref: location.href,
      dismissable: opts.dismissable !== false,
      isOpen: opts.isOpen,
      close: opts.close,
      navigationClose: opts.navigationClose,
    };

    this.entries.push(entry);

    if (entry.dismissable) {
      this.ensurePlaceholder();
    }

    opts.closed.pipe(take(1)).subscribe(() => {
      this.removeEntry(entry);
      this.scheduleSync();
    });
  }

  // Prototypes are patched because lazy modules get their own MatDialog / NzModalService instances.
  private patchMatDialogOpen(): void {
    type MatDialogOpenArgs = Parameters<MatDialog['open']>;
    type MatDialogOpenReturn = ReturnType<MatDialog['open']>;

    const service = this;
    const originalOpen = MatDialog.prototype.open as (...args: MatDialogOpenArgs) => MatDialogOpenReturn;

    MatDialog.prototype.open = function (this: MatDialog, ...args: MatDialogOpenArgs) {
      const config = args[1] as MatDialogConfig<unknown> | undefined;

      if (!service.isPhone()) {
        return originalOpen.apply(this, args);
      }

      args[1] = { ...config, closeOnNavigation: false };
      const dialogRef = originalOpen.apply(this, args);

      service.registerOpenOverlay({
        isOpen: () => this.openDialogs.includes(dialogRef),
        close: () => {
          dialogRef.close();
          return true;
        },
        closed: dialogRef.afterClosed(),
        dismissable: service.shouldHandleMatDialog(config),
        navigationClose: config?.closeOnNavigation !== false ? () => dialogRef.close() : undefined,
      });

      return dialogRef;
    } as MatDialog['open'];
  }

  // confirm/info/success/error/warning all go through create().
  private patchNzModalCreate(): void {
    type NzModalCreateArgs = Parameters<NzModalService['create']>;
    type NzModalCreateReturn = ReturnType<NzModalService['create']>;

    const service = this;
    const originalCreate = NzModalService.prototype.create as (...args: NzModalCreateArgs) => NzModalCreateReturn;

    NzModalService.prototype.create = function (this: NzModalService, ...args: NzModalCreateArgs) {
      const config = args[0] as ModalOptions | undefined;

      // Template <nz-modal> is registered by NzModalMobileBackButtonDirective.
      if (!service.isPhone() || config?.nzViewContainerRef?.element.nativeElement.tagName === 'NZ-MODAL') {
        return originalCreate.apply(this, args);
      }

      args[0] = { ...config, nzCloseOnNavigation: false };
      const modalRef = originalCreate.apply(this, args);

      service.registerOpenOverlay({
        isOpen: () => this.openModals.includes(modalRef),
        close: () => {
          modalRef.close();
          return true;
        },
        closed: modalRef.afterClose,
        dismissable: service.shouldHandleNzModal(config),
        navigationClose: config?.nzCloseOnNavigation !== false ? () => modalRef.close() : undefined,
      });

      return modalRef;
    } as NzModalService['create'];
  }

  private patchNzDrawerCreate(): void {
    type NzDrawerCreateArgs = Parameters<NzDrawerService['create']>;
    type NzDrawerCreateReturn = ReturnType<NzDrawerService['create']>;

    const service = this;
    const originalCreate = NzDrawerService.prototype.create as (...args: NzDrawerCreateArgs) => NzDrawerCreateReturn;

    NzDrawerService.prototype.create = function (this: NzDrawerService, ...args: NzDrawerCreateArgs) {
      const config = args[0] as NzDrawerOptions | undefined;

      if (!service.isPhone()) {
        return originalCreate.apply(this, args);
      }

      args[0] = { ...config, nzCloseOnNavigation: false };
      const drawerRef = originalCreate.apply(this, args);
      const drawer = drawerRef as unknown as NzDrawerComponent;

      service.registerOpenOverlay({
        // Opens after a setTimeout, so rely on afterClose instead of drawer.isOpen.
        isOpen: () => true,
        close: () => {
          // Same as the close button: respects nzOnCancel.
          drawer.nzOnClose.emit();
          return true;
        },
        closed: drawerRef.afterClose,
        navigationClose: config?.nzCloseOnNavigation !== false ? () => drawerRef.close() : undefined,
      });

      return drawerRef;
    } as NzDrawerService['create'];
  }

  private shouldHandleMatDialog(config?: MatDialogConfig<unknown>): boolean {
    const mobileBackButtonClose = this.getMobileBackButtonCloseFlag(config?.data);

    return (
      !this.hasDisabledClass(config?.panelClass) &&
      mobileBackButtonClose !== false &&
      (!config?.disableClose || mobileBackButtonClose === true)
    );
  }

  private shouldHandleNzModal(config?: ModalOptions): boolean {
    return (
      !this.hasDisabledClass(config?.nzClassName) &&
      !this.hasDisabledClass(config?.nzWrapClassName) &&
      this.getMobileBackButtonCloseFlag(config?.nzData) !== false
    );
  }

  private hasDisabledClass(classes?: string | string[]): boolean {
    if (!classes) {
      return false;
    }

    const classList = Array.isArray(classes) ? classes : classes.split(' ');
    return classList.includes(MOBILE_DIALOG_BACK_BUTTON_DISABLED_CLASS);
  }

  private getMobileBackButtonCloseFlag(data: unknown): boolean | undefined {
    if (!data || typeof data !== 'object') {
      return undefined;
    }

    return (data as MobileDialogBackButtonData).mobileBackButtonClose;
  }

  private getPageId(): unknown {
    return history.state?.ɵrouterPageId;
  }

  private ensurePlaceholder(): void {
    // A pending history.back() would pop a new placeholder; handlePopState pushes it afterwards instead.
    if (this.removingPlaceholder || this.isOnPlaceholder()) {
      return;
    }

    this.pushPlaceholder();
  }

  private isOnPlaceholder(): boolean {
    return !!this.placeholderId && history.state?.[this.historyStateKey] === this.placeholderId;
  }

  private pushPlaceholder(): void {
    this.idCounter += 1;
    this.placeholderId = `overlay-${this.idCounter}`;
    this.placeholderHref = location.href;

    history.pushState(
      {
        ...(history.state || {}),
        [this.historyStateKey]: this.placeholderId,
      },
      '',
      location.href
    );
  }

  // Deferred so close-then-open in the same task (e.g. confirm -> next dialog) keeps the placeholder.
  private scheduleSync(): void {
    if (this.syncTimeoutId !== null) {
      window.clearTimeout(this.syncTimeoutId);
    }

    this.syncTimeoutId = window.setTimeout(() => {
      this.syncTimeoutId = null;
      this.removePlaceholderIfIdle();
    });
  }

  private removePlaceholderIfIdle(): void {
    // A running navigation would be cancelled by history.back(); retried on NavigationEnd/Cancel/Error.
    if (!this.placeholderId || this.hasDismissableEntries() || this.router.currentNavigation()) {
      return;
    }

    if (!this.isOnPlaceholder()) {
      // Left behind by a navigation; skipped in handlePopState when Back lands on it.
      this.placeholderId = null;
      return;
    }

    this.placeholderId = null;
    this.removingPlaceholder = true;
    history.back();
  }

  private handlePopState(): void {
    const previousPageId = this.lastPageId;
    const pageId = this.getPageId();
    const stateId = history.state?.[this.historyStateKey];
    this.lastPageId = pageId;
    this.skipDeadPlaceholder = false;

    if (this.removingPlaceholder) {
      this.removingPlaceholder = false;

      if (this.hasDismissableEntries()) {
        this.pushPlaceholder();
      }
      return;
    }

    const samePage =
      location.href === this.placeholderHref || (typeof pageId === 'number' && pageId === previousPageId);

    if (this.placeholderId && stateId !== this.placeholderId && samePage) {
      this.placeholderId = null;
      this.handleBack();
      return;
    }

    this.placeholderId = null;
    this.closeEntriesForNavigation();

    if (!stateId) {
      return;
    }

    if (this.hasDismissableEntries()) {
      this.placeholderId = stateId;
      this.placeholderHref = location.href;
      return;
    }

    // Dead placeholder reached by Back: skip it once the router is done, otherwise it copies this state onto
    // the previous entry. Reached by Forward (same page id): leave it.
    if (typeof previousPageId !== 'number' || typeof pageId !== 'number' || previousPageId > pageId) {
      this.skipDeadPlaceholder = true;
    }
  }

  private handleBack(): void {
    const top = this.getTopOpenEntry();

    if (!top) {
      return;
    }

    if (top.dismissable && top.close()) {
      this.removeEntry(top);
    }

    // Also swallows Back when a non-dismissable overlay is on top of a dismissable one.
    if (this.hasDismissableEntries()) {
      this.pushPlaceholder();
    }
  }

  private closeEntriesForNavigation(): void {
    [...this.entries].forEach(entry => {
      if (entry.navigationClose && entry.isOpen() && location.href !== entry.openedHref) {
        entry.navigationClose();
      }
    });
  }

  private getTopOpenEntry(): MobileOverlayEntry | undefined {
    for (let i = this.entries.length - 1; i >= 0; i--) {
      const entry = this.entries[i];

      if (entry.isOpen()) {
        return entry;
      }

      this.entries.splice(i, 1);
    }

    return undefined;
  }

  private removeEntry(entry: MobileOverlayEntry): void {
    const index = this.entries.indexOf(entry);

    if (index >= 0) {
      this.entries.splice(index, 1);
    }
  }

  private hasDismissableEntries(): boolean {
    return this.entries.some(entry => entry.dismissable && entry.isOpen());
  }
}
